import * as d3 from 'd3'
import type { MutableRefObject } from 'react'
import { useEffect, useMemo, useRef } from 'react'

import type { BidLadderChartProps, ChartDimensions, ChartScales } from '@/features/bidding/types/bidLadder'
import { calculateScales } from '@/features/bidding/utils/cascading/bidLadder/calculateScales'
import { renderAxes } from '@/features/bidding/utils/cascading/bidLadder/renderAxes'
import { renderButterflyBidLadder } from '@/features/bidding/utils/cascading/bidLadder/renderButterflyBidLadder'
import { renderCenterLine } from '@/features/bidding/utils/cascading/bidLadder/renderCenterLine'
import { renderClearingRange } from '@/features/bidding/utils/cascading/bidLadder/renderClearingRange'
import { renderGridLines } from '@/features/bidding/utils/cascading/bidLadder/renderGridLines'
import { renderLegend } from '@/features/bidding/utils/cascading/bidLadder/renderLegend'
import { renderMarketLabels } from '@/features/bidding/utils/cascading/bidLadder/renderMarketLabels'
import { renderOpportunityCostLine } from '@/features/bidding/utils/cascading/bidLadder/renderOpportunityCostLines'

export function useD3BidLadder(
  containerRef: MutableRefObject<HTMLDivElement | null>,
  props: BidLadderChartProps,
  dimensions: ChartDimensions,
) {
  const svgRef = useRef<d3.Selection<SVGSVGElement, unknown, null, undefined>>()

  // Track visibility of opportunity cost and clearing ranges
  const visibilityStateRef = useRef<Map<string, boolean>>(
    new Map([
      ['Clearing Range Down', true],
      ['Clearing Range Up', true],
      ['Opportunity Cost', false],
    ]),
  )

  const scales = useMemo(
    () => calculateScales(props, dimensions, visibilityStateRef.current),
    [props, dimensions, visibilityStateRef.current],
  )

  useEffect(() => {
    if (!containerRef.current) return

    // No tooltip needed - labels are shown directly on the chart

    const handleToggle = () => {
      // Force recalculation of scales
      const newScales = calculateScales(props, dimensions, visibilityStateRef.current)
      renderChart(containerRef.current!, props, dimensions, newScales, visibilityStateRef, handleToggle)
    }

    renderChart(containerRef.current, props, dimensions, scales, visibilityStateRef, handleToggle)

    // Store svg reference
    svgRef.current = d3.select(containerRef.current).select('svg')

    // Cleanup function
    return () => {
      // No cleanup needed - labels are SVG elements
    }
  }, [props, dimensions, scales])

  return svgRef.current
}

function renderChart(
  container: HTMLDivElement,
  props: BidLadderChartProps,
  dimensions: ChartDimensions,
  scales: ChartScales,
  visibilityStateRef: MutableRefObject<Map<string, boolean>>,
  onToggle: () => void,
) {
  const {
    bidLevels,
    primaryForecastData,
    referenceForecastData,
    showClearingRanges = true,
    showOpportunityCosts = false,
    opportunityCost,
  } = props
  const { width, height, margin } = dimensions

  // Clear existing SVG
  d3.select(container).selectAll('*').remove()

  // Create SVG with viewBox for responsive scaling
  // The viewBox defines the coordinate system, width/height control the display size
  // Using xMidYMid meet maintains aspect ratio - browser handles smooth CSS scaling
  const svg = d3
    .select(container)
    .append('svg')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('width', '100%')
    .attr('height', '100%')
    .style('background', '#ffffff')

  // Define gradient for area shading
  const defs = svg.append('defs')
  const gradient = defs
    .append('linearGradient')
    .attr('id', 'area-gradient')
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%')

  gradient.append('stop').attr('offset', '0%').attr('stop-color', '#000000').attr('stop-opacity', 0.3)

  gradient.append('stop').attr('offset', '100%').attr('stop-color', '#000000').attr('stop-opacity', 0)

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

  // Add grid lines
  renderGridLines(g, scales)

  // Store clearing range elements for toggling
  let clearingRangeUpElement: d3.Selection<SVGRectElement, unknown, null, undefined> | undefined
  let clearingRangeDownElement: d3.Selection<SVGRectElement, unknown, null, undefined> | undefined

  // Add clearing ranges if enabled
  if (showClearingRanges) {
    // UP market clearing range (right side) - primaryForecastData
    if (primaryForecastData) {
      clearingRangeUpElement = renderClearingRange(g, scales, primaryForecastData, 'up', visibilityStateRef.current)
    }
    // DOWN market clearing range (left side) - referenceForecastData
    if (referenceForecastData) {
      clearingRangeDownElement = renderClearingRange(
        g,
        scales,
        referenceForecastData,
        'down',
        visibilityStateRef.current,
      )
    }
  }

  // Store line element for toggling
  let oppCostLineElement: d3.Selection<SVGLineElement, unknown, null, undefined> | undefined

  // Add opportunity cost line if enabled
  if (showOpportunityCosts && opportunityCost !== undefined) {
    const isVisible = visibilityStateRef.current.get('Opportunity Cost') ?? true
    oppCostLineElement = renderOpportunityCostLine(g, scales, opportunityCost, isVisible)
  }

  // Separate bids by market
  const upBids = bidLevels.filter((b) => !b.market || b.market === 'up')
  const downBids = bidLevels.filter((b) => b.market === 'down')

  // Draw bid ladder with cumulative lines
  if (upBids.length > 0) {
    renderButterflyBidLadder(g, upBids, 'up', scales)
  }
  if (downBids.length > 0) {
    renderButterflyBidLadder(g, downBids, 'down', scales)
  }

  // Add center line
  renderCenterLine(g, scales)

  // Add axes
  renderAxes(g, scales)

  // Click handlers disabled to allow hover tooltips to work
  // addClickHandlers(g, scales, onChartClick, onChartRightClick)

  // Build combined legend items
  const legendItems: Array<{
    name: string
    type: 'range' | 'line'
    color: string
    visible: boolean
    price?: number
    element?: any
  }> = []

  // Add clearing ranges to legend
  if (showClearingRanges) {
    if (referenceForecastData && clearingRangeDownElement) {
      legendItems.push({
        name: 'Clearing Range Down',
        type: 'range',
        color: '#dc2626',
        visible: visibilityStateRef.current.get('Clearing Range Down') ?? true,
        element: clearingRangeDownElement,
      })
    }
    if (primaryForecastData && clearingRangeUpElement) {
      legendItems.push({
        name: 'Clearing Range Up',
        type: 'range',
        color: '#059669',
        visible: visibilityStateRef.current.get('Clearing Range Up') ?? true,
        element: clearingRangeUpElement,
      })
    }
  }

  // Add opportunity cost to legend
  if (showOpportunityCosts && opportunityCost !== undefined && oppCostLineElement) {
    legendItems.push({
      name: 'Opportunity Cost',
      type: 'line',
      price: opportunityCost,
      color: '#8b5cf6',
      visible: visibilityStateRef.current.get('Opportunity Cost') ?? true,
      element: oppCostLineElement,
    })
  }

  // Render all legends at bottom
  if (legendItems.length > 0) {
    renderLegend(g, scales, legendItems, visibilityStateRef, onToggle, 'bottom')
  }

  // Add market labels
  if (upBids.length > 0 || downBids.length > 0) {
    renderMarketLabels(g, scales, upBids.length > 0, downBids.length > 0)
  }
}
